import React, { useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';

// User's custom video
const HERO_VIDEO = "https://customer-assets.emergentagent.com/job_pizza-premium-2/artifacts/b9m14t60_570983_Pizza_Food_Table_1280x720.mp4";
const HERO_FALLBACK = "https://images.pexels.com/photos/1566837/pexels-photo-1566837.jpeg";

const HeroSection = ({ onScrollToMenu }) => {
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const { scrollY } = useScroll();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Parallax effects
  const opacity = useTransform(scrollY, [0, 400], [1, 0]);
  const scale = useTransform(scrollY, [0, 400], [1, 1.1]);
  const textY = useTransform(scrollY, [0, 400], [0, 100]);

  return (
    <section 
      id="hero" 
      data-testid="hero-section"
      className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden"
    >
      {/* Video Background */}
      <motion.div 
        className="absolute inset-0 z-0"
        style={{ scale }}
      >
        {/* Video for desktop */}
        <video 
          autoPlay 
          muted 
          loop 
          playsInline
          poster={HERO_FALLBACK}
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        {/* Overlay gradient - adapts to theme */}
        <div className={`absolute inset-0 ${
          isDark 
            ? 'bg-gradient-to-b from-black/80 via-black/60 to-[#0A0A0A]' 
            : 'bg-gradient-to-b from-black/50 via-black/30 to-white'
        }`} />
        {/* Grain texture overlay */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
        }} />
      </motion.div>

      {/* Hero Content */}
      <motion.div 
        className="relative z-10 text-center px-4 max-w-4xl mx-auto"
        style={{ y: textY, opacity }}
      >
        {/* Animated Title */}
        <motion.h1 
          data-testid="hero-title"
          className="font-cormorant text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
          initial={{ opacity: 0, y: 50 }}
          animate={isMounted ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
        >
          <span className="block">{t('hero_title').split(' ').slice(0, 2).join(' ')}</span>
          <span className="text-[#C8102E] block mt-2">{t('hero_title').split(' ').slice(2).join(' ')}</span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p 
          data-testid="hero-subtitle"
          className="font-outfit text-lg sm:text-xl text-white/70 mb-10 max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={isMounted ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          {t('hero_subtitle')}
        </motion.p>

        {/* CTA Button */}
        <motion.button
          data-testid="view-menu-btn"
          onClick={onScrollToMenu}
          className="group relative inline-flex items-center gap-2 px-8 py-4 bg-[#008000] text-white font-semibold rounded-full overflow-hidden transition-all duration-300 hover:bg-[#009900] hover:shadow-[0_0_30px_rgba(0,128,0,0.5)]"
          initial={{ opacity: 0, y: 30 }}
          animate={isMounted ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.9 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <span className="relative z-10">{t('view_menu')}</span>
          <ChevronDown className="w-5 h-5 relative z-10 animate-bounce" />
        </motion.button>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10"
        initial={{ opacity: 0 }}
        animate={isMounted ? { opacity: 1 } : {}}
        transition={{ delay: 1.2 }}
        style={{ opacity }}
      >
        <motion.div
          className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center p-2"
          animate={{ y: [0, 5, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <motion.div 
            className="w-1.5 h-1.5 bg-[#008000] rounded-full"
            animate={{ y: [0, 16, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
